import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CreditCard,
  MapPin,
  Wallet,
  Building2,
} from "lucide-react";
import { useFundWallet } from "@privy-io/react-auth";
import { polygon, polygonAmoy } from "@privy-io/chains";
import { getChainConfig } from "@/config/contracts";
import { POLYGON_CHAIN_ID } from "@/config/activeChain";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { OffRampParaguay } from "./OffRampParaguay";
import { OffRampUSA } from "./OffRampUSA";
import { OffRampLatam } from "./OffRampLatam";
import { OffRampP2P } from "./OffRampP2P";
import { SendTokenForm } from "./SendTokenForm";
import { DepositFromBinance } from "./DepositFromBinance";

type Flow = "onramp" | "offramp";
type WithdrawMethod = null | "wallet" | "exchange";
type DepositMethod = null | "binance";

interface RegionConfig {
  code: string;
  label: string;
  flag: string;
  currency: string;
  group: "global" | "latam" | "asia" | "mea";
  offRampType: "exchange" | "eldorado" | "p2p" | "paraguay";
}

const REGIONS: RegionConfig[] = [
  // Global Markets
  { code: "US", label: "USA", flag: "\u{1F1FA}\u{1F1F8}", currency: "USD", group: "global", offRampType: "exchange" },
  { code: "GB", label: "United Kingdom", flag: "\u{1F1EC}\u{1F1E7}", currency: "GBP", group: "global", offRampType: "exchange" },
  { code: "CA", label: "Canada", flag: "\u{1F1E8}\u{1F1E6}", currency: "CAD", group: "global", offRampType: "exchange" },
  { code: "AU", label: "Australia", flag: "\u{1F1E6}\u{1F1FA}", currency: "AUD", group: "global", offRampType: "exchange" },
  { code: "NZ", label: "New Zealand", flag: "\u{1F1F3}\u{1F1FF}", currency: "NZD", group: "global", offRampType: "exchange" },
  { code: "DE", label: "Deutschland", flag: "\u{1F1E9}\u{1F1EA}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "FR", label: "France", flag: "\u{1F1EB}\u{1F1F7}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "ES", label: "España", flag: "\u{1F1EA}\u{1F1F8}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "IT", label: "Italia", flag: "\u{1F1EE}\u{1F1F9}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "NL", label: "Nederland", flag: "\u{1F1F3}\u{1F1F1}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "PT", label: "Portugal", flag: "\u{1F1F5}\u{1F1F9}", currency: "EUR", group: "global", offRampType: "exchange" },
  { code: "CH", label: "Schweiz", flag: "\u{1F1E8}\u{1F1ED}", currency: "CHF", group: "global", offRampType: "exchange" },
  { code: "SE", label: "Sverige", flag: "\u{1F1F8}\u{1F1EA}", currency: "SEK", group: "global", offRampType: "exchange" },
  { code: "PL", label: "Polska", flag: "\u{1F1F5}\u{1F1F1}", currency: "PLN", group: "global", offRampType: "exchange" },
  // Latin America
  { code: "AR", label: "Argentina", flag: "\u{1F1E6}\u{1F1F7}", currency: "ARS", group: "latam", offRampType: "eldorado" },
  { code: "BR", label: "Brasil", flag: "\u{1F1E7}\u{1F1F7}", currency: "BRL", group: "latam", offRampType: "eldorado" },
  { code: "CO", label: "Colombia", flag: "\u{1F1E8}\u{1F1F4}", currency: "COP", group: "latam", offRampType: "eldorado" },
  { code: "PE", label: "Perú", flag: "\u{1F1F5}\u{1F1EA}", currency: "PEN", group: "latam", offRampType: "eldorado" },
  { code: "BO", label: "Bolivia", flag: "\u{1F1E7}\u{1F1F4}", currency: "BOB", group: "latam", offRampType: "eldorado" },
  { code: "PY", label: "Paraguay", flag: "\u{1F1F5}\u{1F1FE}", currency: "PYG", group: "latam", offRampType: "paraguay" },
  { code: "CL", label: "Chile", flag: "\u{1F1E8}\u{1F1F1}", currency: "CLP", group: "latam", offRampType: "p2p" },
  { code: "MX", label: "México", flag: "\u{1F1F2}\u{1F1FD}", currency: "MXN", group: "latam", offRampType: "p2p" },
  { code: "UY", label: "Uruguay", flag: "\u{1F1FA}\u{1F1FE}", currency: "UYU", group: "latam", offRampType: "p2p" },
  { code: "CR", label: "Costa Rica", flag: "\u{1F1E8}\u{1F1F7}", currency: "CRC", group: "latam", offRampType: "p2p" },
  { code: "DO", label: "Rep. Dominicana", flag: "\u{1F1E9}\u{1F1F4}", currency: "DOP", group: "latam", offRampType: "p2p" },
  { code: "SV", label: "El Salvador", flag: "\u{1F1F8}\u{1F1FB}", currency: "USD", group: "latam", offRampType: "p2p" },
  // Asia Pacific
  { code: "SG", label: "Singapore", flag: "\u{1F1F8}\u{1F1EC}", currency: "SGD", group: "asia", offRampType: "exchange" },
  { code: "KR", label: "South Korea", flag: "\u{1F1F0}\u{1F1F7}", currency: "KRW", group: "asia", offRampType: "p2p" },
  { code: "IN", label: "India", flag: "\u{1F1EE}\u{1F1F3}", currency: "INR", group: "asia", offRampType: "p2p" },
  { code: "PH", label: "Philippines", flag: "\u{1F1F5}\u{1F1ED}", currency: "PHP", group: "asia", offRampType: "p2p" },
  { code: "VN", label: "Vietnam", flag: "\u{1F1FB}\u{1F1F3}", currency: "VND", group: "asia", offRampType: "p2p" },
  { code: "ID", label: "Indonesia", flag: "\u{1F1EE}\u{1F1E9}", currency: "IDR", group: "asia", offRampType: "p2p" },
  { code: "TH", label: "Thailand", flag: "\u{1F1F9}\u{1F1ED}", currency: "THB", group: "asia", offRampType: "p2p" },
  { code: "MY", label: "Malaysia", flag: "\u{1F1F2}\u{1F1FE}", currency: "MYR", group: "asia", offRampType: "p2p" },
  // Middle East & Africa
  { code: "AE", label: "UAE", flag: "\u{1F1E6}\u{1F1EA}", currency: "AED", group: "mea", offRampType: "p2p" },
  { code: "IL", label: "Israel", flag: "\u{1F1EE}\u{1F1F1}", currency: "ILS", group: "mea", offRampType: "exchange" },
  { code: "TR", label: "Türkiye", flag: "\u{1F1F9}\u{1F1F7}", currency: "TRY", group: "mea", offRampType: "p2p" },
  { code: "NG", label: "Nigeria", flag: "\u{1F1F3}\u{1F1EC}", currency: "NGN", group: "mea", offRampType: "p2p" },
  { code: "ZA", label: "South Africa", flag: "\u{1F1FF}\u{1F1E6}", currency: "ZAR", group: "mea", offRampType: "p2p" },
  { code: "KE", label: "Kenya", flag: "\u{1F1F0}\u{1F1EA}", currency: "KES", group: "mea", offRampType: "p2p" },
];

const GROUP_LABELS: Record<string, string> = {
  global: "fundModal.globalMarkets",
  latam: "fundModal.latinAmerica",
  asia: "fundModal.asiaPacific",
  mea: "fundModal.middleEastAfrica",
};

const GROUP_ORDER = ["global", "latam", "asia", "mea"] as const;

/** Detect user's country via free IP geolocation API */
async function detectCountry(): Promise<string | null> {
  try {
    const res = await fetch("https://ipapi.co/country_code/", { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return null;
    const code = (await res.text()).trim().toUpperCase();
    return code.length === 2 ? code : null;
  } catch {
    return null;
  }
}

interface FundWalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletAddress: string;
  defaultFlow?: Flow;
}

function FundWalletModal({
  open,
  onOpenChange,
  walletAddress,
  defaultFlow = "onramp",
}: FundWalletModalProps) {
  const { t } = useTranslation();
  const [selectedRegion, setSelectedRegion] = useState<RegionConfig | null>(null);
  const [flow, setFlow] = useState<Flow>(defaultFlow);
  const [withdrawMethod, setWithdrawMethod] = useState<WithdrawMethod>(null);
  const [depositMethod, setDepositMethod] = useState<DepositMethod>(null);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState<string>("25");
  const { fundWallet } = useFundWallet();

  // Sync flow when defaultFlow prop changes (e.g. opening modal from Withdraw button)
  useEffect(() => {
    setFlow(defaultFlow);
  }, [defaultFlow]);

  // Detect country on first open
  useEffect(() => {
    if (open && !detectedCountry) {
      detectCountry().then(setDetectedCountry);
    }
  }, [open, detectedCountry]);

  // Auto-select region if detected country matches (only when inside exchange flow)
  useEffect(() => {
    if (detectedCountry && flow === "offramp" && withdrawMethod === "exchange" && !selectedRegion) {
      const match = REGIONS.find((r) => r.code === detectedCountry);
      if (match) {
        setSelectedRegion(match);
      }
    }
  }, [detectedCountry, flow, withdrawMethod, selectedRegion]);

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedRegion(null);
      setWithdrawMethod(null);
      setDepositMethod(null);
      setFlow(defaultFlow);
    }
    onOpenChange(isOpen);
  };

  const DEPOSIT_AMOUNTS = ['25', '50', '100', '250'] as const;

  // Privy requires a fixed `amount` when using erc20 asset format —
  // no amount picker is shown in the on-ramp UI.
  const handleDeposit = async () => {
    if (!walletAddress) return;
    // Chain and token must come from the SAME config: getChainConfig() follows
    // the active chain (see config/activeChain.ts), so reading the chain from
    // the env var here would fund an Amoy wallet with a Polygon USDT address.
    const cfg = getChainConfig();
    const fundingChain = cfg.chainId === POLYGON_CHAIN_ID ? polygon : polygonAmoy;
    await fundWallet({
      address: walletAddress,
      options: {
        chain: fundingChain,
        amount: depositAmount,
        asset: { erc20: cfg.tokenAddresses.USDT as `0x${string}` },
      },
    });
  };

  const renderOffRamp = () => {
    if (!selectedRegion) return null;

    if (selectedRegion.offRampType === "eldorado") {
      return <OffRampLatam currencyCode={selectedRegion.currency} />;
    }

    if (selectedRegion.offRampType === "paraguay") {
      return <OffRampParaguay />;
    }

    if (selectedRegion.offRampType === "exchange") {
      return <OffRampUSA regionCode={selectedRegion.code} />;
    }

    return <OffRampP2P currencyCode={selectedRegion.currency} countryName={selectedRegion.label} />;
  };

  // Group regions and sort detected country first within its group
  const groupedRegions = GROUP_ORDER.map((group) => {
    const regions = REGIONS.filter((r) => r.group === group);
    if (detectedCountry) {
      regions.sort((a, b) => {
        if (a.code === detectedCountry) return -1;
        if (b.code === detectedCountry) return 1;
        return 0;
      });
    }
    return { group, regions };
  });

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={flow === "onramp" ? t("fundModal.addFunds", "Add Funds") : t("fundModal.withdrawFunds", "Withdraw Funds")}
      className="max-h-[85vh] overflow-y-auto"
    >
      <div className="mt-4 space-y-4">
        {/* Flow toggle */}
        <div className="flex gap-1 rounded-lg bg-surface-secondary p-1">
          <button
            onClick={() => { setFlow("onramp"); setSelectedRegion(null); setWithdrawMethod(null); setDepositMethod(null); }}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              flow === "onramp"
                ? "bg-surface text-text-primary shadow-sm"
                : "text-text-muted hover:text-text-secondary"
            }`}
          >
            <ArrowDownToLine className="h-4 w-4" />
            {t("fundModal.deposit", "Deposit")}
          </button>
          <button
            onClick={() => { setFlow("offramp"); setSelectedRegion(null); setWithdrawMethod(null); setDepositMethod(null); }}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              flow === "offramp"
                ? "bg-surface text-text-primary shadow-sm"
                : "text-text-muted hover:text-text-secondary"
            }`}
          >
            <ArrowUpFromLine className="h-4 w-4" />
            {t("fundModal.withdraw", "Withdraw")}
          </button>
        </div>

        {/* ON-RAMP */}
        {flow === "onramp" && depositMethod === null && (
          <div className="space-y-4">
            <div className="rounded-xl bg-gradient-to-r from-brand-500/10 to-success-500/10 p-4">
              <p
                className="text-sm text-text-secondary"
                dangerouslySetInnerHTML={{ __html: t("fundModal.buyUsdtDesc", "Buy <strong>USDT</strong> with your credit/debit card, Apple Pay or Google Pay. Works worldwide — your country is detected automatically.") }}
              />
            </div>

            <div className="flex gap-2">
              {DEPOSIT_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setDepositAmount(amt)}
                  className={`flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${
                    depositAmount === amt
                      ? "border-brand-500 bg-brand-500/10 text-brand-500"
                      : "border-border-subtle text-text-muted hover:border-brand-500/50 hover:text-text-secondary"
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>

            <Button
              onClick={handleDeposit}
              className="w-full gap-2"
              size="lg"
            >
              <CreditCard className="h-5 w-5" />
              {t("fundModal.buyUsdt", "Buy USDT")} — ${depositAmount}
            </Button>

            <p className="text-center text-xs text-text-muted">
              {t("fundModal.poweredByPrivy", "Powered by Privy. Funds arrive directly in your wallet.")}
            </p>

            {walletAddress && (
              <button
                onClick={() => setDepositMethod("binance")}
                className="flex w-full items-center gap-3 rounded-xl border border-border-subtle p-3 text-left transition-all hover:border-brand-500 hover:bg-brand-500/5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning-50 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">
                    {t("fundModal.binanceTile", "Send from Binance")}
                  </p>
                  <p className="text-xs text-text-muted">
                    {t("fundModal.binanceTileHint", "No card fee")}
                  </p>
                </div>
                <span className="text-text-muted">{"→"}</span>
              </button>
            )}
          </div>
        )}

        {/* ON-RAMP: Binance sub-panel */}
        {flow === "onramp" && depositMethod === "binance" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDepositMethod(null)}
              className="gap-1.5 text-text-muted"
            >
              {"←"} {t("fundModal.back", "Back")}
            </Button>

            <h3 className="text-base font-medium text-text-primary">
              {t("ramp.binanceDepositTitle", "Send from Binance")}
            </h3>

            <DepositFromBinance walletAddress={walletAddress} />
          </div>
        )}

        {/* OFF-RAMP: Choose method */}
        {flow === "offramp" && withdrawMethod === null && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setWithdrawMethod("wallet")}
              className="flex flex-col items-center gap-3 rounded-xl border border-border-subtle p-5 text-center transition-all hover:border-brand-500 hover:bg-brand-500/5 hover:shadow-sm"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                <Wallet className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {t("fundModal.toYourWallet", "To your wallet")}
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  {t("fundModal.toYourWalletDesc", "MetaMask, Ledger, etc.")}
                </p>
              </div>
            </button>
            <button
              onClick={() => setWithdrawMethod("exchange")}
              className="flex flex-col items-center gap-3 rounded-xl border border-border-subtle p-5 text-center transition-all hover:border-brand-500 hover:bg-brand-500/5 hover:shadow-sm"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-warning-50 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {t("fundModal.toBank", "To bank account")}
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  {t("fundModal.toBankDesc", "Via El Dorado, Coinbase, etc.")}
                </p>
              </div>
            </button>
          </div>
        )}

        {/* OFF-RAMP: Transfer to own wallet */}
        {flow === "offramp" && withdrawMethod === "wallet" && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWithdrawMethod(null)}
              className="gap-1.5 text-text-muted"
            >
              {"\u2190"} {t("fundModal.back", "Back")}
            </Button>

            <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 dark:border-warning-700 dark:bg-warning-900/20">
              <p className="text-xs text-warning-700 dark:text-warning-300">
                {t("fundModal.polygonWarning", "Only send to a Polygon network address. Sending to a different network may result in permanent loss of funds.")}
              </p>
            </div>

            <SendTokenForm
              title={t("fundModal.sendToYourWallet", "Send to your wallet")}
            />
          </div>
        )}

        {/* OFF-RAMP: Exchange — Region selector */}
        {flow === "offramp" && withdrawMethod === "exchange" && !selectedRegion && (
          <div className="space-y-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWithdrawMethod(null)}
              className="gap-1.5 text-text-muted"
            >
              {"\u2190"} {t("fundModal.back", "Back")}
            </Button>

            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-text-muted" />
              <span className="text-sm text-text-secondary">
                {t("fundModal.whereToReceive", "Where do you want to receive funds?")}
              </span>
            </div>

            {groupedRegions.map(({ group, regions }) => (
              <div key={group} className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                  {t(GROUP_LABELS[group]!, group)}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {regions.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => setSelectedRegion(r)}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 transition-all hover:border-brand-500 hover:bg-brand-500/5 hover:shadow-sm ${
                        detectedCountry === r.code
                          ? "border-brand-500 bg-brand-500/5 shadow-sm"
                          : "border-border-subtle"
                      }`}
                    >
                      <span className="text-2xl">{r.flag}</span>
                      <span className="text-xs font-medium text-text-primary">{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OFF-RAMP: Exchange — Region content */}
        {flow === "offramp" && withdrawMethod === "exchange" && selectedRegion && (
          <>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedRegion(null); setWithdrawMethod(null); }}
                className="gap-1.5 text-text-muted"
              >
                {"\u2190"} {t("fundModal.back", "Back")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedRegion(null)}
                className="gap-1.5 text-text-muted"
              >
                {selectedRegion.flag} {selectedRegion.label} — {t("fundModal.changeRegion", "Change region")}
              </Button>
            </div>

            {renderOffRamp()}
          </>
        )}
      </div>
    </Modal>
  );
}

export { FundWalletModal };
export type { Flow };
